import type { ApiClient } from '../apiClient';

export interface SealContext {
  message: { id: string; vault_id: string; format: string; recipient_id: string | null };
  recipient: { id: string; is_placeholder: boolean; public_key: string | null; public_key_suite: string | null } | null;
  owner: { public_key: string; public_key_suite: string };
  vault_dek_b64: string;
  cipher_suite: string;
}
export interface PresignResult { url: string; key: string; requiredHeaders: Record<string, string>; expiresInSeconds: number; }
export type DeliveryKeyHolder = 'recipient' | 'owner';

/** Full backend SealResult shape (mirrors src/api/messages seal.service.ts). */
export interface SealResult {
  message: { id: string; status: 'sealed'; sealed_at: string; delivery_key_holder: DeliveryKeyHolder };
  polygon_anchor: { queued: boolean; pending_confirmation: boolean };
}

/**
 * Local mirror of the vendored `SealApiClient` (src/client/messaging/seal.ts).
 * Kept self-contained here — no cross-project import — so Task 5 can pass
 * `makeSealApiClient(api)` into `runSealFlow` and have the compiler enforce it.
 */
export interface SealApiClient {
  fetchSealContext(messageId: string): Promise<SealContext>;
  postSeal(input: PostSealInput): Promise<SealResult>;
}
export interface PostSealInput {
  messageId: string;
  cekRecipientWrap: string;
  cekVaultWrap: string;
  recipientPublicKey: string;
  deliveryKeyHolder: DeliveryKeyHolder;
  cekAlgorithmVersion: string;
  letterCiphertextB64?: string;
  ivB64?: string;
  /** SHA-256 of the ciphertext that passed the client's self-test. */
  ciphertextSha256?: string;
  /** Plaintext length that round-tripped during that self-test. */
  plaintextBytes?: number;
}

export function createMessage(
  api: ApiClient,
  args: { vaultId: string; recipientId: string | null; format?: 'video' | 'letter' },
): Promise<{ message: { id: string } }> {
  return api.post('/api/messages', {
    vault_id: args.vaultId,
    recipient_id: args.recipientId,
    format: args.format ?? 'video',
  });
}
export function getSealContext(api: ApiClient, messageId: string): Promise<SealContext> {
  return api.get(`/api/messages/${messageId}/seal-context`);
}
export function presignUpload(api: ApiClient, messageId: string, contentLengthBytes: number): Promise<PresignResult> {
  return api.post(`/api/messages/${messageId}/media/presigned`, { content_length_bytes: contentLengthBytes });
}
export async function putCiphertext(args: { url: string; headers: Record<string, string>; body: Uint8Array }): Promise<void> {
  // Uint8Array<ArrayBufferLike> vs DOM's BodyInit: TS 5.7+ typed-array/DOM lib friction, not a real type hazard.
  const res = await fetch(args.url, { method: 'PUT', headers: args.headers, body: args.body as BodyInit });
  if (!res.ok) throw new Error(`Upload failed (${res.status}).`);
}
export function finalizeMedia(api: ApiClient, messageId: string, args: { contentLengthBytes: number; codec: string; container: string; durationSeconds: number; ivB64: string; width?: number; height?: number }): Promise<{ media_file: { processing_status: 'uploaded' } }> {
  return api.post(`/api/messages/${messageId}/media/finalize`, {
    content_length_bytes: args.contentLengthBytes,
    codec: args.codec,
    container: args.container,
    duration_seconds: args.durationSeconds,
    iv_b64: args.ivB64,
    ...(args.width !== undefined ? { width: args.width } : {}),
    ...(args.height !== undefined ? { height: args.height } : {}),
  });
}
export function postSeal(api: ApiClient, input: PostSealInput): Promise<SealResult> {
  return api.post(`/api/messages/${input.messageId}/seal`, {
    cek_recipient_wrap: input.cekRecipientWrap,
    cek_vault_wrap: input.cekVaultWrap,
    recipient_public_key: input.recipientPublicKey,
    delivery_key_holder: input.deliveryKeyHolder,
    cek_algorithm_version: input.cekAlgorithmVersion,
    ...(input.letterCiphertextB64 !== undefined ? { letter_ciphertext_b64: input.letterCiphertextB64 } : {}),
    ...(input.ivB64 !== undefined ? { iv_b64: input.ivB64 } : {}),
    // The proof that this ciphertext decrypted back to exactly what was
    // recorded, computed a moment ago while the key was still in memory. It is
    // the only evidence that will exist, years from now, that the message was
    // ever openable — so it has to actually leave the browser. It did not,
    // until a production seal was checked and the column came back empty.
    ...(input.ciphertextSha256 !== undefined ? { ciphertext_sha256: input.ciphertextSha256 } : {}),
    ...(input.plaintextBytes !== undefined ? { plaintext_bytes: input.plaintextBytes } : {}),
  });
}
/**
 * When a message should reach someone.
 *
 * The API has always supported all of these (`ConfigureTriggerInput` in
 * src/api/messages/triggers/configure.service.ts). Until now the app only ever
 * sent `date`, which meant the age trigger — the one that lets a message arrive
 * when someone *becomes* a certain age rather than on a calendar day — was
 * unreachable in the product.
 */
export type LifeEvent =
  | 'graduation'
  | 'wedding'
  | 'first_child'
  | 'birthday_18'
  | 'birthday_21'
  | 'custom';

export type FallbackAction =
  | 'escalate_to_guardians'
  | 'notify_creator'
  | 'release_unconditional'
  | 'archive';

export type DeliveryCondition =
  | { kind: 'date'; date: string }
  | { kind: 'age'; age: number }
  | {
      kind: 'life_event';
      event: LifeEvent;
      /** The "but" — what to do if the moment never comes. */
      fallbackDate?: string;
      fallbackAction?: FallbackAction;
    };

export type Combinator = 'AND' | 'OR';

/**
 * Create a compound trigger GROUP (AND/OR) on a vault. Individual triggers then
 * attach to it via `trigger_group_id`. AND = every member must be satisfied
 * before delivery; OR = the first satisfied member fires (siblings cancel).
 */
export function createTriggerGroup(
  api: ApiClient,
  vaultId: string,
  args: { combinator: Combinator; description?: string },
): Promise<{ trigger_group: { id: string } }> {
  return api.post(`/api/vaults/${vaultId}/trigger-groups`, {
    combinator: args.combinator,
    ...(args.description !== undefined ? { description: args.description } : {}),
  });
}

export function configureTrigger(
  api: ApiClient,
  messageId: string,
  args: { condition: DeliveryCondition; triggerTimezone: string; triggerGroupId?: string },
): Promise<unknown> {
  const { condition, triggerTimezone, triggerGroupId } = args;
  const group = triggerGroupId !== undefined ? { trigger_group_id: triggerGroupId } : {};

  if (condition.kind === 'age') {
    return api.post(`/api/messages/${messageId}/triggers`, {
      trigger_type: 'age',
      target_age: condition.age,
      trigger_timezone: triggerTimezone,
      ...group,
    });
  }

  if (condition.kind === 'life_event') {
    return api.post(`/api/messages/${messageId}/triggers`, {
      trigger_type: 'life_event',
      life_event_type: condition.event,
      trigger_timezone: triggerTimezone,
      ...(condition.fallbackDate !== undefined ? { fallback_date: condition.fallbackDate } : {}),
      ...(condition.fallbackAction !== undefined
        ? { fallback_action: condition.fallbackAction }
        : {}),
      ...group,
    });
  }

  return api.post(`/api/messages/${messageId}/triggers`, {
    trigger_type: 'date',
    trigger_date: condition.date,
    trigger_timezone: triggerTimezone,
    ...group,
  });
}

/** Adapt the authed client to the vendored SealApiClient (fetchSealContext + postSeal). */
export function makeSealApiClient(api: ApiClient): SealApiClient {
  return {
    fetchSealContext: (messageId: string) => getSealContext(api, messageId),
    postSeal: (input: PostSealInput) => postSeal(api, input),
  };
}
